import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import SpotlightLabel from "./SpotlightLabel";
import { useReactFlow, useStore } from "@xyflow/react";

type NodeBox = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

type Hint = {
  id: string[];
  hint: string;
};

type NodeSpotlightOverlayProps = {
  hints: Hint[];
  isOpen: boolean;
  onFinish?: () => void;
};

export const NodeSpotlightOverlay = ({
  hints,
  isOpen,
  onFinish,
}: NodeSpotlightOverlayProps) => {
  const { getNode, fitView } = useReactFlow();
  const [boxes, setBoxes] = useState<NodeBox[]>([]);
  const [showLabel, setShowLabel] = useState<boolean>(true);

  const [transform] = useStore((store) => [store.transform]);
  const [container] = useStore((store) => [store.domNode]);
  const containerRect = container?.getBoundingClientRect();

  const [active, setActive] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const currentHint = hints[currentIndex] ?? { id: [], hint: "" };
  const isLast = currentIndex === hints.length - 1;

  const isFirst = currentIndex === 0;

  const handleNext = () => {
    if (!isLast) setCurrentIndex((i) => i + 1);
    else {
      setShowLabel(false);
      onFinish?.();
    }
  };

  const handlePrev = () => {
    if (!isFirst) setCurrentIndex((i) => i - 1);
  };

  useEffect(() => {
    if (isOpen && hints.length && !active) {
      const timeout = setTimeout(() => {
        setActive(true);
        setCurrentIndex(0);
      }, 300);
      return () => clearTimeout(timeout);
    }
    if (!isOpen) {
      setActive(false);
      setCurrentIndex(0);
    }
  }, [isOpen, hints.length]);

  useEffect(() => {
    if (!containerRect || !currentHint?.id) return;

    const newBoxes: NodeBox[] = [];
    const cNodes = [];
    for (const id of currentHint?.id) {
      const node = getNode(id);
      if (!node) continue;
      cNodes.push(node);
      const nodeEl = container?.querySelector(
        `[data-id="${node.id}1"]`,
      ) as HTMLElement;

      if (!nodeEl) continue;

      const rect = nodeEl.getBoundingClientRect();

      newBoxes.push({
        id,
        x: rect.left - containerRect.left,
        y: rect.top - containerRect.top,
        width: rect.width,
        height: rect.height,
      });
    }
    setBoxes(newBoxes);
    fitView({ nodes: cNodes, duration: 100 });
  }, [currentHint?.id.join(","), transform, currentHint]);

  useEffect(() => {
    if (currentHint && containerRect) {
      const cNodes = [];
      for (const id of currentHint?.id) {
        const node = getNode(id);
        if (!node) continue;
        cNodes.push(node);
      }
    }
  }, [currentIndex]);

  if (!boxes.length) return null;

  return (
    <>
      <motion.svg
        className="absolute top-0 left-0 w-full h-full pointer-events-none"
        style={{ zIndex: 1000 }}
        initial={{ opacity: 0, scale: 1 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <defs>
          <mask id="spotlight-mask">
            <rect width="100%" height="100%" fill="white" />
            {boxes.map((box, index) =>
              false ? (
                <motion.rect
                  key={box.id}
                  x={box.x}
                  y={box.y}
                  width={box.width}
                  height={box.height}
                  fill="black"
                  stroke={"var(--accent-primary)"}
                  rx={12}
                  ry={12}
                  initial={{ scale: 5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{
                    duration: 0.4,
                    ease: "easeOut",
                  }}
                />
              ) : (
                <rect
                  key={box.id}
                  x={box.x}
                  y={box.y}
                  width={box.width}
                  height={box.height}
                  fill="black"
                  rx={12}
                  ry={12}
                />
              ),
            )}
          </mask>
        </defs>

        <rect
          width="100%"
          height="100%"
          fill="black"
          fillOpacity={0.6}
          mask="url(#spotlight-mask)"
        />

        {boxes.map((box) => (
          <rect
            key={`border-${box.id}`}
            x={box.x}
            y={box.y}
            width={box.width}
            height={box.height}
            rx={5}
            fill="none"
            stroke="var(--accent-primary)"
            strokeWidth={1.5}
          />
        ))}
      </motion.svg>
      {/* === ✅ POPUP LABEL === */}
      {showLabel && (
        <div
          className="absolute z-[1001]   w-42 "
          style={{
            bottom: 0,
            right: 0,
          }}
        >
          <SpotlightLabel
            hint={currentHint?.hint}
            onNext={handleNext}
            onPrev={handlePrev}
            onFinish={() => {
              onFinish?.();
              setShowLabel(false);
            }}
            isFirst={isFirst}
            isLast={isLast}
            currentStep={currentIndex + 1}
            totalSteps={hints.length}
          />
        </div>
      )}
    </>
  );
};
